// pipe (info tips): the interface-segregation seam that lets the shared ERP presenters render a
// "what is this?" tip WITHOUT depending on the app's tip implementation.
//
// The real ⓘ affordance (@affine/core's ErpInfoTip) reads a module-level preference store through
// @toeverything/infra — a dependency this package must never take (the portal consumes this package
// without core or infra). So the moved presenters (ErpPageHeader, TableFilterInput) render through
// THIS slot: the host app provides its tip component once via `ErpInfoTipProvider` (core mounts it
// in the ErpScopeBoundary every ERP route wraps), and a consumer with no provider — the portal, a
// bare spec render — gets nothing, never a crash and never a second tip implementation.
import {
  type ComponentType,
  createContext,
  type ReactNode,
  useContext,
} from 'react';

/** The narrow contract the shared presenters need — core's richer ErpInfoTip props satisfy it. */
export interface ErpInfoTipSlotProps {
  content?: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Extra class for the default ⓘ trigger (spacing at call sites). */
  className?: string;
  'data-testid'?: string;
}

const ErpInfoTipContext = createContext<ComponentType<ErpInfoTipSlotProps> | null>(
  null
);

/** Host-app registration point: `<ErpInfoTipProvider value={ErpInfoTip}>…</ErpInfoTipProvider>`. */
export const ErpInfoTipProvider = ErpInfoTipContext.Provider;

/** Renders the host app's tip component, or nothing when none is provided (fail quiet). */
export function ErpInfoTipSlot(props: ErpInfoTipSlotProps) {
  const InfoTip = useContext(ErpInfoTipContext);
  return InfoTip ? <InfoTip {...props} /> : null;
}
